import React, { useContext } from 'react';
import classNames from 'classnames';
import { useCmsMegaMenuQuery } from '@/api/cms/cms';
import { Button, Drawer, Icon, useDrawer } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetLayout } from '@/generated/layout';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { useLogout } from '@/generated/user';
import { useIsMobile } from '@/helpers';
import { DrawerMegaMenuItem } from '@/types/drawerMegaMenuItem';
import { CorporateLink } from './CorporateLink';
import { DesktopMainMenu } from './DesktopMainMenu';
import { DesktopSubMenu } from './DesktopSubMenu';
import { DrawerMegaMenu } from './DrawerMegaMenu';
import styles from './DrawerMenu.module.scss';
import { SegmentBar } from './SegmentBar';

interface DrawerMenuProps {
	triggerButtonClassName?: string;
}

const drawerWidth = {
	narrow: 375,
	wide: 976,
};

export const DrawerMenu: React.FunctionComponent<DrawerMenuProps> = ({ triggerButtonClassName }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: layout } = useGetLayout(segmentationId);
	const { data: settings } = useGetSettings(segmentationId);
	const user = settings?.data.user;
	const { data: translations } = useGetTranslations(segmentationId);
	const ariaControlId = 'drawer-menu';

	const isSalesPortal = settings?.data.isSalesPortal ?? true;
	const { data: cmsMegaMenu } = useCmsMegaMenuQuery(settings?.data, settings?.data.cmsSettings?.cmsMegaMenu || '');

	const drawerMegaMenu: DrawerMegaMenuItem[] = React.useMemo(() => {
		return [...(layout?.data.megaMenu.items ?? []), ...(cmsMegaMenu ?? [])];
	}, [cmsMegaMenu, layout?.data.megaMenu.items]);

	const isMobile = useIsMobile();

	const drawerRef = React.useRef<HTMLDivElement>(null);
	const { isOpen, isRendered, handleOpen, handleClose } = useDrawer(drawerRef);

	const [isWide, setIsWide] = React.useState(false);
	const [activeItem, setActiveItem] = React.useState<number | undefined>(undefined);

	const logout = useLogout();

	const handleLogout = React.useCallback((): void => {
		logout.mutate(undefined, {
			onSuccess: () => {
				window.location.reload();
			},
		});
	}, [logout]);

	const openSubmenu = (index: number): void => {
		setActiveItem(index);
		setIsWide(true);
	};

	const closeDrawer = (): void => {
		handleClose();
		setActiveItem(undefined);
		setIsWide(false);
	};

	return (
		<>
			{/* Trigger button */}
			<Button
				className={triggerButtonClassName}
				variant={'ghost'}
				hasOnlyIcon
				onClick={handleOpen}
				aria-label={translations?.data.shared.menuLabel || 'Menu'}
				aria-expanded={isOpen}
				aria-controls={ariaControlId}
			>
				<Icon name={'burger'} size={'md'} />
			</Button>

			{/* Drawer menu */}

			{isRendered && (
				<Drawer
					desktopWidth={`${isWide ? drawerWidth.wide : drawerWidth.narrow}px`}
					drawerRef={drawerRef}
					isOpen={isOpen}
					id={ariaControlId}
				>
					<Drawer.Header handleClose={closeDrawer} />
					<Drawer.Body>
						{isMobile ? (
							<DrawerMegaMenu
								megaMenu={drawerMegaMenu}
								isMenuOpen={isOpen}
								handleCloseMenu={handleClose}
								isSalesPortal={isSalesPortal}
							/>
						) : (
							<nav
								className={classNames(styles.desktopMenuWrapper, { [styles.isWide]: isWide })}
								role={'navigation'}
								aria-label={`${translations?.data.shared.navigation.primary} ${translations?.data.shared.navigation.nav}`}
							>
								<div className={styles.colMain}>
									<DesktopMainMenu
										megaMenu={drawerMegaMenu}
										activeItem={activeItem}
										openSubmenu={openSubmenu}
										closeDrawer={closeDrawer}
									/>
									{isSalesPortal && <CorporateLink />}
								</div>
								<div className={styles.colSub}>
									{activeItem !== undefined &&
									layout?.data.megaMenu.items[activeItem]?.showLinkToSelector ? (
										<SegmentBar closeDrawer={closeDrawer} />
									) : null}
									<ul className={styles.listSub}>
										{drawerMegaMenu?.map((item, index) => (
											<DesktopSubMenu
												item={item}
												isActive={index === activeItem}
												closeDrawer={closeDrawer}
												key={`${index}-${item.name}`}
											/>
										))}
									</ul>
								</div>
							</nav>
						)}
					</Drawer.Body>
					{user !== undefined && isSalesPortal ? (
						<Drawer.Footer hasNoPadding>
							<Button
								className={styles.footerButton}
								variant={'ghost'}
								isFullWidth
								onClick={handleLogout}
							>
								<Icon name={'logout'} size={'md'} />
								<span>{translations?.data.auth.signin.logout}</span>
							</Button>
						</Drawer.Footer>
					) : null}
				</Drawer>
			)}
		</>
	);
};
